import { c as _c } from "react/compiler-runtime";
import { createRef, type FC, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useAdvGalleryPlacement, useAdvSponsorshipThumbnail } from '@sbt-web/adv';
import { getCategoryByID } from '@sbt-web/category-utils';
import { useClientHints, useViewport } from '@sbt-web/hooks';
import { ItemCategory, type AdItem } from '@sbt-web/network/types';
import { Button, Close, Divider } from '@sbt-web/ui';
import { shouldReduceDataUse } from '@sbt-web/utils';
import { LazyVideoPreroll } from '@/client/components/Adv/GPT/sdkIntegration';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import Carousel from './Carousel';
import ownClasses from './styles.module.scss';
const Thumbnails = dynamic(() => import('./Thumbnails'), {
  ssr: false
});
export const buildImageViewTrackingData = (category: ItemCategory, adUrn: string, current: number, total: number) => {
  const macroCat = getCategoryByID(category.parentId);
  const baseData: {
    type: 'Engagement';
    name: string;
    object: Record<string, string | Record<string, unknown>>;
  } = {
    type: 'Engagement',
    name: 'View Image in Addetail',
    object: {
      '@id': `sdrn:subito:classified:gallery:element:gallery-image-${current + 1}/${Math.max(total, 1)}`,
      '@type': 'UIElement',
      elementType: 'Img',
      relatedTo: {
        '@type': 'ClassifiedAd',
        '@id': `sdrn:subito:classified:${adUrn}`,
        category: `${macroCat.friendly}>${category.friendlyName}`
      }
    }
  };
  const imageElement = document.getElementById(`gallery-cell-${current}`)?.querySelector('img');
  if (imageElement != null) {
    baseData.object.size = `${imageElement.width}x${imageElement.height}`;
  }
  return baseData;
};
interface Props {
  ad: AdItem;
}
const GalleryComponent: FC<Props> = t0 => {
  const $ = _c(51);
  const {
    ad
  } = t0;
  const {
    images,
    category,
    subject: alt,
    urn: adUrn
  } = ad;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createRef();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const containerRef = t1;
  const [currentIndex, setCurrentIndex] = useState(0);
  const {
    isMobile,
    isDesktop
  } = useViewport();
  const {
    effectiveConnectionType,
    saveData,
    deviceMemory
  } = useClientHints();
  const {
    noAdvToggle
  } = useAdvToggle();
  let t2;
  if ($[1] !== deviceMemory || $[2] !== effectiveConnectionType || $[3] !== saveData) {
    t2 = shouldReduceDataUse({
      effectiveConnectionType,
      saveData,
      deviceMemory
    });
    $[1] = deviceMemory;
    $[2] = effectiveConnectionType;
    $[3] = saveData;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const reduceDataUse = t2;
  let t3;
  let t4;
  if ($[5] !== adUrn || $[6] !== category || $[7] !== currentIndex || $[8] !== images.length) {
    t3 = () => {
      getOrCreatePulse()?.queueEvent(buildImageViewTrackingData(category, adUrn, currentIndex, images.length));
    };
    t4 = [adUrn, category, currentIndex, images.length];
    $[5] = adUrn;
    $[6] = category;
    $[7] = currentIndex;
    $[8] = images.length;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  const {
    advLoaded
  } = useAdvGalleryPlacement();
  const {
    advSponsorshipThumbnail,
    goToAdv,
    setGoToAdv
  } = useAdvSponsorshipThumbnail(images.length, process.env.NEXT_PUBLIC_ASSETS_BASE_URL, ownClasses);
  let t5;
  let t6;
  if ($[11] !== noAdvToggle || $[12] !== setGoToAdv) {
    t5 = () => {
      if (noAdvToggle === "B") {
        setGoToAdv(false);
      }
    };
    t6 = [noAdvToggle, setGoToAdv];
    $[11] = noAdvToggle;
    $[12] = setGoToAdv;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useEffect(t5, t6);
  const [fullScreen, setFullScreen] = useState(false);
  let t7;
  let t8;
  if ($[15] !== fullScreen) {
    t7 = () => {
      if (fullScreen) {
        const handleKeyDown = event => {
          if (event.key === "Escape") {
            setFullScreen(false);
          }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
          document.removeEventListener("keydown", handleKeyDown);
        };
      }
    };
    t8 = [fullScreen];
    $[15] = fullScreen;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  useEffect(t7, t8);
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setFullScreen(true);
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleSwitchFullscreen = t9;
  let t10;
  if ($[19] !== fullScreen) {
    t10 = cn({
      [ownClasses["full-size"]]: fullScreen
    });
    $[19] = fullScreen;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isDesktop || $[22] !== noAdvToggle) {
    t11 = isDesktop && noAdvToggle === "A" ? <LazyVideoPreroll parentRef={containerRef} /> : null;
    $[21] = isDesktop;
    $[22] = noAdvToggle;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== ad || $[25] !== alt || $[26] !== category || $[27] !== currentIndex || $[28] !== fullScreen || $[29] !== goToAdv || $[30] !== images || $[31] !== reduceDataUse || $[32] !== setGoToAdv) {
    t12 = <Carousel images={images} alt={alt} category={category} onChange={setCurrentIndex} currentIndex={currentIndex} reduceDataUse={reduceDataUse} goToAdv={goToAdv} setGoToAdv={setGoToAdv} ad={ad} switchFullscreen={handleSwitchFullscreen} fullScreen={fullScreen} />;
    $[24] = ad;
    $[25] = alt;
    $[26] = category;
    $[27] = currentIndex;
    $[28] = fullScreen;
    $[29] = goToAdv;
    $[30] = images;
    $[31] = reduceDataUse;
    $[32] = setGoToAdv;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== advLoaded || $[35] !== advSponsorshipThumbnail || $[36] !== currentIndex || $[37] !== goToAdv || $[38] !== images || $[39] !== isMobile || $[40] !== reduceDataUse || $[41] !== setGoToAdv) {
    t13 = !isMobile && images.length >= 2 || !isMobile && advLoaded && images.length ? <><Divider spacing="null" classes={[ownClasses["gallery-divider"]]} /><Thumbnails images={images} onClick={index => {
        setCurrentIndex(index);
        setGoToAdv(false);
      }} reduceDataUse={reduceDataUse} currentIndex={currentIndex} advThumbnail={advSponsorshipThumbnail} advLoaded={advLoaded} goToAdv={goToAdv} /></> : null;
    $[34] = advLoaded;
    $[35] = advSponsorshipThumbnail;
    $[36] = currentIndex;
    $[37] = goToAdv;
    $[38] = images;
    $[39] = isMobile;
    $[40] = reduceDataUse;
    $[41] = setGoToAdv;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== fullScreen) {
    t14 = fullScreen && <Button design="outline-white" size="large" aria-label="Chiudi Gallery Fullscreen" onClick={() => setFullScreen(false)} icon={<Close width={24} height={24} />} classes={[ownClasses["close-dialog"]]} tabIndex={0} />;
    $[43] = fullScreen;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t14) {
    t15 = <div ref={containerRef} className={t10}>{t11}{t12}{t13}{t14}</div>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  return t15;
};
export default GalleryComponent;
export type { Props };